import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Play, Loader2 } from "lucide-react";
import DestinationGallery from "../components/DestinationGallery";
import { toast } from "sonner";
import { SelectionChips, ProgressBar } from "../components/FeedbackComponents";
import { useLanguage } from "../contexts/LanguageContext";
import { getQuestionTranslation } from "../i18n/questionTranslations";
import { t } from "../i18n/translations";
import "../styles/QuizPage.css";
import "../styles/FeedbackChips.css";
import "../styles/DestinationGallery.css";
// Import local questions as fallback
import { questions as localQuestions, sectionTransition } from "../data/questions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions to detect video type
const isCloudinaryUrl = (url) => {
  return url && url.includes('cloudinary.com');
};

const isYoutubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

const QuizPage = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state;
  const { language } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [destinations, setDestinations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [highlightedDestinations, setHighlightedDestinations] = useState([]);
  const [showVideoTransition, setShowVideoTransition] = useState(false);
  const [galleryPreviewCount, setGalleryPreviewCount] = useState(15);
  const [quizVideo, setQuizVideo] = useState(null);
  const [adTimer, setAdTimer] = useState(10);
  const [canSkip, setCanSkip] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [showUnmutePrompt, setShowUnmutePrompt] = useState(true);
  
  // NEW: State for dynamically loaded questions
  const [questions, setQuestions] = useState(localQuestions);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [quizStartTime] = useState(Date.now());

  // Reset timer when video screen appears
  useEffect(() => {
    if (showVideoTransition) {
      setAdTimer(10);
      setCanSkip(false);
      setVideoMuted(true);
      setShowUnmutePrompt(true);
    }
  }, [showVideoTransition]);

  // Timer countdown for ad skip
  useEffect(() => {
    if (showVideoTransition && adTimer > 0) {
      const timer = setTimeout(() => {
        setAdTimer(adTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showVideoTransition && adTimer === 0) {
      setCanSkip(true);
    }
  }, [showVideoTransition, adTimer]);

  // Fetch questions from database
  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions/active`);
      const dbQuestions = response.data.questions || [];
      
      if (dbQuestions.length > 0) {
        // Transform DB questions to match local format
        const transformedQuestions = dbQuestions.map(q => ({
          id: q.id,
          section: q.section,
          question: q.question_de, // Default to German, translation handled separately
          question_de: q.question_de,
          question_en: q.question_en,
          question_es: q.question_es,
          options: q.options.map(opt => ({
            value: opt.value,
            label: opt.label_de, // Default to German
            label_de: opt.label_de,
            label_en: opt.label_en,
            label_es: opt.label_es
          }))
        }));
        
        console.log('[OK] Loaded ' + transformedQuestions.length + ' questions from database');
        setQuestions(transformedQuestions);
      } else {
        console.log('[WARN] No questions in database, using local fallback');
        setQuestions(localQuestions);
      }
    } catch (error) {
      console.error("Error fetching questions from API, using fallback:", error);
      setQuestions(localQuestions);
    } finally {
      setQuestionsLoading(false);
    }
  };

  useEffect(() => {
    fetchDestinations();
    initializeDestinations();
    fetchAlgorithmSettings();
    fetchQuizVideo();
    fetchQuestions(); // NEW: Load questions from database
    
    // Restore from localStorage if coming from landing page
    if (locationState?.resumeProgress) {
      const { answers: savedAnswers, currentQuestion: savedQuestion } = locationState.resumeProgress;
      setAnswers(savedAnswers);
      setCurrentQuestion(savedQuestion);
    }
    // Restore answers if coming from results page
    else if (locationState?.resumeAnswers) {
      setAnswers(locationState.resumeAnswers.reduce((acc, ans) => {
        acc[ans.question_id] = ans.answer;
        return acc;
      }, {}));
    }
  }, []);
  
  // Handle goToLastQuestion after questions are loaded
  useEffect(() => {
    if (!questionsLoading && locationState?.goToLastQuestion && questions.length > 0) {
      setCurrentQuestion(questions.length - 1);
    }
  }, [questionsLoading, questions.length, locationState?.goToLastQuestion]);

  useEffect(() => {
    if (destinations.length > 0 && Object.keys(answers).length > 0) {
      updateHighlightedDestinations();
    }
  }, [answers]);

  // Track quiz abandon when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const answeredCount = Object.keys(answers).length;
      if (answeredCount > 0 && answeredCount < questions.length) {
        const currentQ = questions[currentQuestion];
        navigator.sendBeacon(`${API}/quiz/abandon`, JSON.stringify({
          answers: Object.entries(answers).map(([qid, ans]) => ({ question_id: qid, answer: ans })),
          language: language,
          abandoned_at_question: currentQ?.id || 'unknown',
          time_spent_seconds: Math.round((Date.now() - quizStartTime) / 1000)
        }));
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [answers, currentQuestion, questions, language, quizStartTime]);

  const fetchAlgorithmSettings = async () => {
    try {
      const response = await axios.get(`${API}/algorithm/settings`);
      setGalleryPreviewCount(response.data.gallery_preview_count);
    } catch (error) {
      console.error("Error fetching algorithm settings:", error);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations`);
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error("Error fetching destinations:", error);
    }
  };

  const initializeDestinations = async () => {
    try {
      await axios.post(`${API}/init-destinations`);
    } catch (error) {
      console.error("Error initializing destinations:", error);
    }
  };

  const fetchQuizVideo = async () => {
    try {
      const response = await axios.get(`${API}/videos/quiz`);
      setQuizVideo(response.data.video);
    } catch (error) {
      console.error("Error fetching quiz video:", error);
    }
  };

  // Get localized quiz video title
  const getQuizVideoTitle = () => {
    if (!quizVideo) return t('quiz.videoCardTitle');
    if (language === 'en' && quizVideo.title_en) return quizVideo.title_en;
    if (language === 'es' && quizVideo.title_es) return quizVideo.title_es;
    return quizVideo.title || t('quiz.videoCardTitle');
  };

  // Get localized quiz video description
  const getQuizVideoDescription = () => {
    if (!quizVideo) return t('quiz.videoCardDescription');
    if (language === 'en' && quizVideo.description_en) return quizVideo.description_en;
    if (language === 'es' && quizVideo.description_es) return quizVideo.description_es;
    return quizVideo.description || t('quiz.videoCardDescription');
  };

  // Get localized partner button text
  const getPartnerButtonText = () => {
    if (!quizVideo) return null;
    if (language === 'en' && quizVideo.partner_button_en) return quizVideo.partner_button_en;
    if (language === 'es' && quizVideo.partner_button_es) return quizVideo.partner_button_es;
    return quizVideo.partner_button_de || 'Mehr erfahren';
  };

  const updateHighlightedDestinations = async () => {
    // Build answers array for backend preview
    const answersArray = Object.entries(answers)
      .filter(([_, v]) => v)
      .map(([qId, answer]) => ({ question_id: qId, answer }));
    
    if (answersArray.length === 0) {
      setHighlightedDestinations([]);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/quiz/preview`, {
        answers: answersArray,
        language: language
      });
      
      if (response.data?.preview) {
        // Merge preview data with full destination data for images/details
        const previewDests = response.data.preview.map(p => {
          const fullDest = destinations.find(d => d.name === p.name);
          return fullDest ? { ...fullDest, match_quality: p.match_quality, preview_score: p.score } : { ...p };
        });
        setHighlightedDestinations(previewDests);
      }
    } catch (error) {
      console.error('Preview error:', error);
      // Fallback: show all destinations unsorted
      setHighlightedDestinations(destinations.slice(0, galleryPreviewCount));
    }
  };

  // Mapping functions for coded values to German values
  const mapSeasonToPeriod = (answer) => {
    // Mapping from coded values to German periods
    const seasonMapping = {
      'jan_feb': 'Januar-Februar',
      'mar_apr': 'März-April',
      'may_jun': 'Mai-Juni',
      'jul_aug': 'Juli-August',
      'sep_oct': 'September-Oktober',
      'nov_dec': 'November-Dezember'
    };
    
    // If it's a coded value, map it
    if (seasonMapping[answer]) {
      return [seasonMapping[answer]];
    }
    
    // If it's already a German value, use directly
    const validPeriods = [
      'Januar-Februar', 'März-April', 'Mai-Juni',
      'Juli-August', 'September-Oktober', 'November-Dezember'
    ];
    return validPeriods.includes(answer) ? [answer] : [];
  };

  const mapClimateValue = (answer) => {
    const climateMapping = {
      'tropical': 'tropisch & warm',
      'mediterranean': 'mediterran & mild',
      'temperate': 'gemäßigt & kühl',
      'dry': 'trocken & sonnig',
      'alpine': 'alpin & polar'
    };
    return climateMapping[answer] || answer;
  };

  const mapScenarioValue = (answer) => {
    const scenarioMapping = {
      'beach': 'Strand',
      'rainforest': 'Regenwald',
      'mountains': 'Berge',
      'city': 'City',
      'desert': 'Wüste'
    };
    return scenarioMapping[answer] || answer;
  };

  const mapCulturalValue = (answer) => {
    const culturalMapping = {
      'yoga': 'Yoga & Spiritualität',
      'dance': 'Tanz & Musik',
      'culinary': 'Kulinarik & Wein',
      'art': 'Kunst & Design'
    };
    return culturalMapping[answer] || answer;
  };

  const handleAnswer = (value) => {
    const currentQ = questions[currentQuestion];
    const previousAnswer = answers[currentQ.id];
    
    // Update answers
    const newAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(newAnswers);

    // Check if we need to show video transition
    if (currentQuestion === sectionTransition.afterQuestion - 1 && sectionTransition.videoPlaceholder) {
      setShowVideoTransition(true);
      return;
    }

    // Auto-advance
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        handleSubmit(newAnswers);
      }
    }, 300);
  };

  const handleContinueAfterVideo = () => {
    setShowVideoTransition(false);
    setCurrentQuestion(currentQuestion + 1);
    // Reset timer for next time
    setAdTimer(10);
    setCanSkip(false);
  };

  // UPDATED: handleSubmit now saves results to localStorage as backup
  const handleSubmit = async (answersToSubmit = answers) => {
    setIsSubmitting(true);
    try {
      const answersList = Object.entries(answersToSubmit).map(
        ([question_id, answer]) => ({
          question_id,
          answer,
        })
      );

      console.log('[SUBMIT] Submitting quiz answers:', answersList);

      const response = await axios.post(`${API}/quiz/submit`, {
        answers: answersList,
        language: language,
        time_spent_seconds: Math.round((Date.now() - quizStartTime) / 1000),
      });

      const recommendations = response.data.recommended_destinations;
      
      console.log('[RECEIVED] Received recommendations:', recommendations?.length || 0, 'destinations');

      // CRITICAL FIX: Save results to localStorage as backup
      // This ensures results are available even if location.state is lost
      const resultsData = {
        recommendations: recommendations,
        answers: answersList,
        language: language,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('quiz_results', JSON.stringify(resultsData));
      console.log('[SAVED] Saved quiz results to localStorage');

      navigate("/results", {
        state: {
          recommendations: recommendations,
          answers: answersList,
          language: language,
        },
      });
    } catch (error) {
      console.error("Error submitting quiz:", error);
      toast.error(t('quiz.errorMessage', language) || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      setIsSubmitting(false);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  // Get current question with translations
  const currentQ = questions[currentQuestion];
  
  // Smart translation: Use DB fields if available, otherwise fall back to static translations
  const getTranslatedQuestion = () => {
    if (!currentQ) return { question: '', options: [] };
    
    // Check if this is a DB question with multilingual fields
    if (currentQ.question_de && currentQ.question_en && currentQ.question_es) {
      const questionText = language === 'de' ? currentQ.question_de 
                         : language === 'en' ? currentQ.question_en 
                         : language === 'es' ? currentQ.question_es 
                         : currentQ.question_de;
      
      const translatedOptions = currentQ.options.map(opt => ({
        value: opt.value,
        label: language === 'de' ? opt.label_de 
             : language === 'en' ? opt.label_en 
             : language === 'es' ? opt.label_es 
             : opt.label_de || opt.label
      }));
      
      return { question: questionText, options: translatedOptions };
    }
    
    // Fall back to static translations for local questions
    return getQuestionTranslation(currentQ.id, language);
  };
  
  const translatedQuestion = getTranslatedQuestion();

  // Show loading state while questions are loading
  if (questionsLoading) {
    return (
      <div className="quiz-container" data-testid="quiz-page">
        <div className="quiz-loading" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '20px'
        }}>
          <Loader2 size={48} className="animate-spin" style={{ color: '#c084fc' }} />
          <p style={{ color: '#6b7280' }}>
            {language === 'de' ? 'Fragen werden geladen...' 
             : language === 'en' ? 'Loading questions...' 
             : 'Cargando preguntas...'}
          </p>
        </div>
      </div>
    );
  }

  // Video Transition Screen
  if (showVideoTransition) {
    return (
      <div className="quiz-container" data-testid="quiz-page">
        <div className="video-transition-screen">
          <div className="video-content">
            <h2 className="transition-title">
              {t('quiz.videoTitle', language)}
            </h2>
            
            {quizVideo ? (
              <div className="quiz-video-container">
                <div className="quiz-video-card">
                  <div className="quiz-video-thumbnail">
                    {isCloudinaryUrl(quizVideo.url) ? (
                      // Cloudinary video player
                      <video
                        src={quizVideo.url}
                        autoPlay
                        muted={videoMuted}
                        controls
                        playsInline
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          objectFit: 'contain',
                          background: '#000'
                        }}
                      />
                    ) : (
                      // YouTube iframe
                      <iframe
                        key={videoMuted ? 'muted' : 'unmuted'}
                        src={`${quizVideo.url}?autoplay=1&mute=${videoMuted ? 1 : 0}&enablejsapi=1&rel=0&controls=1`}
                        title={quizVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                      ></iframe>
                    )}
                    
                    {/* Unmute prompt button - ALWAYS clickable, above everything */}
                    {videoMuted && (
                      <button
                        className="video-unmute-btn"
                        onClick={() => {
                          setVideoMuted(false);
                          setShowUnmutePrompt(false);
                        }}
                        style={{
                          position: 'absolute',
                          top: '20px',
                          right: '20px',
                          backgroundColor: 'rgba(139, 92, 246, 0.95)',
                          color: 'white',
                          border: '2px solid white',
                          borderRadius: '50%',
                          width: '56px',
                          height: '56px',
                          cursor: 'pointer',
                          zIndex: 20,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.95)';
                        }}
                        title={language === 'de' ? 'Ton aktivieren' : language === 'en' ? 'Enable Sound' : 'Activar Sonido'}
                      >
                        <svg 
                          width="28" 
                          height="28" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                          <line x1="23" y1="9" x2="17" y2="15"></line>
                          <line x1="17" y1="9" x2="23" y2="15"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="quiz-video-content">
                    <h3>{getQuizVideoTitle()}</h3>
                    <p>{getQuizVideoDescription()}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="video-placeholder">
                <Play size={64} className="play-icon" />
                <p className="video-text">Video</p>
              </div>
            )}
            <div className="video-action-buttons" style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
              {quizVideo?.partner_url && (
                <a
                  href={quizVideo.partner_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{textDecoration: 'none', background: 'var(--color-violett)', opacity: 1}}
                  data-testid="partner-link-button"
                >
                  {getPartnerButtonText()}
                </a>
              )}
              <button
                className="btn-primary"
                onClick={handleContinueAfterVideo}
                disabled={!canSkip}
                data-testid="continue-after-video"
              >
                {canSkip 
                  ? t('quiz.continueButton', language) 
                  : t('quiz.continueButtonWait', language).replace('{seconds}', adTimer)
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="quiz-container" data-testid="quiz-page">
      <div className="quiz-layout">
        {/* Animated Destination Gallery - Polaroid Style */}
        <div className="globe-section">
          <DestinationGallery
            destinations={destinations}
            highlightedDestinations={highlightedDestinations}
          />
        </div>

        {/* Quiz Section */}
        <div className="quiz-section">
          {/* Logo and Back Button */}
          <div className="quiz-header">
            <button 
              className="back-to-home-btn"
              onClick={() => {
                // Save progress to localStorage
                localStorage.setItem('quiz_progress', JSON.stringify({
                  answers,
                  currentQuestion,
                  timestamp: new Date().toISOString()
                }));
                navigate('/');
              }}
            >
              ← Zurück zur Startseite
            </button>
            <a href="https://www.animoo.de" target="_blank" rel="noopener noreferrer">
              <img src="/animoo-logo.png" alt="animoo weddings" className="quiz-logo" />
            </a>
          </div>
          
          <div className="quiz-card">
            {/* Section Indicator */}
            <div className="section-indicator">
              <span className={`section-badge ${currentQ.section}`}>
                {currentQ.section === 'destination' ? 
                  (language === 'de' ? 'Zielgebiet' : language === 'en' ? 'Destination' : 'Destino') : 
                  (language === 'de' ? 'Erlebnis' : language === 'en' ? 'Experience' : 'Experiencia')
                }
              </span>
            </div>

            {/* SCHRITT 2: Verbesserte Progress Bar */}
            <ProgressBar current={currentQuestion + 1} total={questions.length} />
            
            {/* SCHRITT 2: Selection Chips - Zeige ab Frage 4 */}
            {currentQuestion >= 3 && (
              <SelectionChips selectedAnswers={answers} questions={questions} />
            )}

            {/* Question */}
            <div className="question-container">
              <h2 className="question-title" data-testid="question-title">
                {translatedQuestion.question}
              </h2>

              <div className="options-container">
                {translatedQuestion.options.map((option) => (
                  <div
                    key={option.value}
                    className={`option-item ${
                      answers[currentQ.id] === option.value ? "selected" : ""
                    }`}
                    onClick={() => handleAnswer(option.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAnswer(option.value);
                      }
                    }}
                    data-testid={`option-${option.value}`}
                  >
                    <div className="option-radio">
                      {answers[currentQ.id] === option.value && (
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: 'var(--color-violett)',
                          margin: '2px'
                        }}></div>
                      )}
                    </div>
                    <span className="option-label">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Info */}
            <div className="quiz-info">
              <button
                onClick={() =>
                  currentQuestion > 0 && setCurrentQuestion(currentQuestion - 1)
                }
                disabled={currentQuestion === 0}
                className="btn-back-small"
                data-testid="back-button"
              >
                <ArrowLeft size={18} />
                {t('quiz.back', language)}
              </button>
              <span className="question-number">
                {t('quiz.progress', language, { current: currentQuestion + 1, total: questions.length })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
